// deps
import React, { memo, useMemo } from "react";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";

// internal-components
import SplitBillTabs from "./SplitBillTabs";
import SplitBillContactListRow from "./SplitBillContactListRow";
import SplitBillDineCashRow from "./SplitBillDineCashRow";

// styles
import {
    Wrapper,
    BillAmountSection,
    GuestsSection,
    GuestsHeader,
    SplitEquallyButton,
    GuestsList,
    AddMoreGuests,
    ButtonContainer,
    ToastContainer,
} from "./styled";

// interfaces
import { ISplitBillFormProps } from "@dineout-payment-success-ui/interfaces";

// constants
import {
    BILL_AMOUNT_LABEL,
    GUESTS_LABEL,
    SPLIT_EQUALLY_LABEL,
    ADD_MORE_GUESTS_TEXT,
    CONFIRM_BUTTON_TEXT,
} from "@dineout-payment-success-ui/constants";
import { INR_CURRENCY_SYMBOL_TEXT } from "@dineout-payment-success-ui/constants/Constants";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";

// utils
import { format } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { getActiveDineCashSplit } from "./splitBillHelpers";

const SplitBillForm: React.FC<ISplitBillFormProps> = ({
    billAmount,
    splitBillContacts,
    splitModeOptions,
    dineCashSplit,
    selectedTab,
    guestSplits,
    validationError,
    isFormValid,
    orderId,
    restaurantId,
    onTabChange,
    onSplitEqually,
    onConfirm,
    onDineCashSelectionChange,
    onValidationDismiss,
    onAddMoreGuests,
    onPercentageChange,
    onAmountChange,
    onInputBlur,
    onShareIncrement,
    onShareDecrement,
}) => {
    const allContacts = useMemo(() => [splitBillContacts.self, ...splitBillContacts.guests], [splitBillContacts]);

    const contactListRowProps = useMemo(
        () => ({
            guestSplits,
            selectedTab,
            billAmount,
            allContacts,
            onPercentageChange,
            onAmountChange,
            onInputBlur,
            onShareIncrement,
            onShareDecrement,
        }),
        [
            guestSplits,
            selectedTab,
            billAmount,
            allContacts,
            onPercentageChange,
            onAmountChange,
            onInputBlur,
            onShareIncrement,
            onShareDecrement,
        ],
    );

    const activeDineCashSplit = useMemo(() => getActiveDineCashSplit(dineCashSplit), [dineCashSplit]);

    return (
        <Wrapper data-testid={SPLIT_BILL_TEST_IDS.FORM}>
            {!isEmpty(validationError) && (
                <ToastContainer>
                    <Toast
                        message={validationError}
                        background="background_Primary"
                        show
                        autoDismiss
                        onDismiss={onValidationDismiss}
                        containerStyle={{
                            width: "100%",
                        }}
                        leftAsset={<SpriteIconWeb iconName="infoFilled16" fillColor="text_Highest_Emphasis" />}
                    />
                </ToastContainer>
            )}

            <BillAmountSection>
                <Text variant="V2.body1.neutral.regular" color="text_Med_Emphasis">
                    {BILL_AMOUNT_LABEL}
                </Text>
                <Text variant="V2.hero.neutral.bold" color="text_Highest_Emphasis">
                    {format(INR_CURRENCY_SYMBOL_TEXT, String(billAmount))}
                </Text>
            </BillAmountSection>

            <SplitBillTabs splitModeOptions={splitModeOptions} selectedTab={selectedTab} onSelectTab={onTabChange} />

            <GuestsSection>
                <GuestsList>
                    <GuestsHeader>
                        <Text variant="V2.body1.neutral.bold" color="text_Med_Emphasis">
                            {format(GUESTS_LABEL, String(allContacts.length))}
                        </Text>
                        <SplitEquallyButton onClick={onSplitEqually} data-testid={SPLIT_BILL_TEST_IDS.SPLIT_EQUALLY}>
                            <SpriteIconWeb
                                iconName="refresh12"
                                fillColor="text_Med_Emphasis"
                                strokeColor="text_Med_Emphasis"
                            />
                            <Text variant="V2.overline.neutral.medium" color="text_Med_Emphasis">
                                {SPLIT_EQUALLY_LABEL}
                            </Text>
                        </SplitEquallyButton>
                    </GuestsHeader>

                    <SplitBillContactListRow
                        key={splitBillContacts.self.number}
                        contact={splitBillContacts.self}
                        {...contactListRowProps}
                    />
                    {splitBillContacts?.guests?.map((contact) => (
                        <SplitBillContactListRow key={contact.number} contact={contact} {...contactListRowProps} />
                    ))}

                    <AddMoreGuests onClick={onAddMoreGuests} data-testid={SPLIT_BILL_TEST_IDS.ADD_GUESTS}>
                        <SpriteIconWeb
                            iconName="addFilled12"
                            fillColor="text_Med_Emphasis"
                            strokeColor="text_Med_Emphasis"
                        />
                        <Text variant="V2.body3.neutral.bold" color="text_Med_Emphasis">
                            {ADD_MORE_GUESTS_TEXT}
                        </Text>
                    </AddMoreGuests>
                </GuestsList>
            </GuestsSection>

            <ButtonContainer>
                {activeDineCashSplit && (
                    <SplitBillDineCashRow
                        dineCashSplit={activeDineCashSplit}
                        onSelectionChange={onDineCashSelectionChange}
                        orderId={orderId}
                        amountPaidByUser={billAmount}
                        numberOfGuest={allContacts.length}
                        restaurantId={restaurantId}
                    />
                )}
                <FullWidthButton
                    variant="primary"
                    type="button"
                    onClick={onConfirm}
                    isDisabled={!isFormValid}
                    data-testid={SPLIT_BILL_TEST_IDS.CONFIRM_BTN}
                >
                    {CONFIRM_BUTTON_TEXT}
                </FullWidthButton>
            </ButtonContainer>
        </Wrapper>
    );
};

export default memo(SplitBillForm);
